/**
 * Cycle-tolerant serialization for log messages.
 *
 * `JSON.stringify` throws on cyclic input — "Converting circular structure to
 * JSON" in V8, "cannot serialize cyclic structures" in WebKit. The objects most
 * worth logging are exactly the cyclic ones: a DOM Event references its
 * `target`, which references the document; an hls.js ErrorData carries
 * `networkDetails` (the XMLHttpRequest) and `frag`.
 *
 * That throw is not a cosmetic problem. It propagates out of the error handler
 * that was trying to log, so any recovery work queued after the log statement
 * never runs. A cyclic `JSON.stringify(err)` inside ContentCard's video error
 * handler skipped the two calls that cleared the loading spinner, leaving a
 * failed video spinning forever (observed in prod RUM, 2026-08-04).
 *
 * `safeStringify` never throws. Cycles become "[Circular]", Errors and DOM
 * nodes get compact readable forms, and any unexpected failure degrades to
 * `String(value)`.
 */
const MAX_LENGTH_DEFAULT = 2000;
/** Compact, readable stand-ins for values JSON.stringify handles badly. */
const describeSpecial = (value) => {
    if (value instanceof Error) {
        // JSON.stringify(new Error("x")) is "{}" — name/message/stack are all
        // non-enumerable, so the useful part is silently dropped.
        return `${value.name}: ${value.message}`;
    }
    if (typeof Event !== "undefined" && value instanceof Event) {
        const target = value.target;
        const tag = (target === null || target === void 0 ? void 0 : target.tagName) ? ` on ${target.tagName}` : "";
        return `[Event ${value.type}${tag}]`;
    }
    if (typeof Node !== "undefined" && value instanceof Node) {
        return `[${value.nodeName}]`;
    }
    return undefined;
};
/**
 * Serializes any value to a string suitable for a log line, without throwing.
 *
 * @param value  Anything — errors, DOM events, hls.js payloads, plain objects.
 * @param maxLength  Truncation cap; log transport is not a debugger.
 */
export const safeStringify = (value, maxLength = MAX_LENGTH_DEFAULT) => {
    var _a;
    let out;
    try {
        const direct = describeSpecial(value);
        if (direct !== undefined) {
            out = direct;
        }
        else {
            const seen = new WeakSet();
            out =
                (_a = JSON.stringify(value, (_key, val) => {
                    const special = describeSpecial(val);
                    if (special !== undefined)
                        return special;
                    if (typeof val === "object" && val !== null) {
                        if (seen.has(val))
                            return "[Circular]";
                        seen.add(val);
                    }
                    if (typeof val === "function")
                        return "[Function]";
                    if (typeof val === "bigint")
                        return val.toString();
                    return val;
                })) !== null && _a !== void 0 ? _a : String(value);
        }
    }
    catch (_b) {
        // Getters that throw, exotic proxies, anything else unforeseen: a log line
        // is never worth failing its caller for.
        try {
            out = String(value);
        }
        catch (_c) {
            out = "[unserializable]";
        }
    }
    return out.length > maxLength
        ? out.slice(0, maxLength) + "…[truncated]"
        : out;
};
